import React, { useEffect, useState, useReducer } from 'react';
import SwiperClass from 'swiper';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';

import { useWindowSize } from '../../use.window.size';
import { LayoutProps } from '../../layouts/layout.props';
import { Field } from '../../layouts/slider/field';
import { FormPage } from '../../layouts/slider/page';
import { Header } from '../../header';
import { ProgressHeader } from '../../ProgressHeader';
import { useSubmission } from '@/components/use.submission';
import SubmitModal from '@/components/SubmitModal';
import {
  ACTIONS,
  SubmissionsState,
  SubmissionsAction,
} from '@/components/layouts/layout.props';

const reducer = (
  submissions: SubmissionsState[],
  action: SubmissionsAction
) => {
  switch (action.type) {
    case ACTIONS.ADD_SUBMISSION:
      let replaced = false;
      const updated = submissions.map((submission: SubmissionsState) => {
        if (submission.field === action.payload.field) {
          replaced = true;
          return action.payload;
        } else {
          return submission;
        }
      });
      if (replaced) {
        return updated;
      } else {
        return [...updated, action.payload];
      }
    case ACTIONS.DELETE_SUBMISSIONS:
      return [];
    default:
      return submissions;
  }
};

export const MLBLayout: React.FC<LayoutProps> = (props) => {
  // const { t } = useTranslation();
  const [swiper, setSwiper] = useState<SwiperClass>();
  const { height } = useWindowSize();
  const { id, design, startPage, endPage, fields, isLive } = props.form;
  const [submissionFlag, setSubmissionFlag] = useState(false); // flag to whether submit the submission or not
  const currentSubmission = useSubmission(id as string);
  const { startSubmission, finish, setField } = currentSubmission;
  const [submissions, dispatch] = useReducer(reducer, []); // overall submissions
  // used to reset answers of each question when user resets form
  // field.tsx - formik doesn't reset the form unless its intialValues prop changes
  const [submissionCounter, setSubmissionCounter] = useState(0);
  const [pathDone, setPathDone] = useState(false);
  const [showProgress, setShowProgress] = useState(!startPage.show);
  const [progress, setProgress] = useState(0);
  const [lastSlideIndex, setLastSlideIndex] = useState(fields.length - 1);

  //track total questions
  const totalQuestions = fields.filter(
    (field) => field.type !== 'hidden'
  ).length;

  useEffect(() => {
    document.title = props.form.title;

    // get the slide index of the last slide that is not hidden
    for (let i = fields.length - 1; i >= 0; i--) {
      if (fields[i].type !== 'hidden') {
        setLastSlideIndex(i);
        break;
      }
    }
  }, []);

  useEffect(() => {
    const submitSubmission = async () => {
      const s: { id: string; token: string } | undefined =
        await startSubmission();
      if (s) {
        await setField('', submissions, s).catch((err) => console.error(err));
        await finish(s);
      }
      setProgress(100);
      setShowProgress(false);
      setSubmissionFlag(false);
      // saving could be really fast so this prevents saving spinner from flashing
      await new Promise((r) => setTimeout(r, 1000));
      goLast();
    };
    if (submissionFlag) {
      submitSubmission();
    }
  }, [submissionFlag]);

  // mui text input (textarea, textfield) does not go back to its default value even though we pass in empty values and re-render the components
  // this hook ensures that when the form is resetted, the values in the text inputs do not hold the previous user inputs
  useEffect(() => {
    if (submissionCounter !== 0) {
      Array.from(document.getElementsByClassName('MuiInputBase-input')).forEach(
        (textfield) => {
          const converted = textfield as HTMLInputElement;
          converted.value = '';
        }
      );
    }
  }, [submissionCounter]);

  const goNext = () => {
    if (!swiper) return;

    // logger('goNext')
    swiper.allowSlideNext = true;
    swiper.slideNext();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    swiper.allowSlideNext = false;
  };
  const goPrev = () => {
    if (!swiper) return;

    // logger('goPrev')
    swiper.allowSlidePrev = true;
    swiper.slidePrev();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    swiper.allowSlidePrev = false;
  };
  const goBeginning = () => {
    if (!swiper) return;

    // logger('goNext')
    setProgress(0);
    setShowProgress(!startPage.show);
    dispatch({
      type: ACTIONS.DELETE_SUBMISSIONS,
      payload: { field: '', data: '' },
    });
    setSubmissionCounter(submissionCounter + 1);
    swiper.allowSlidePrev = true;
    swiper.slideTo(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    swiper.allowSlidePrev = false;
  };
  const goPath1 = () => {
    if (!swiper) return;

    // logger('goNext')
    swiper.allowSlideNext = true;
    swiper.slideTo(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    swiper.allowSlideNext = false;
  };
  const goPath2 = () => {
    if (!swiper) return;

    // logger('goNext')
    swiper.allowSlideNext = true;
    swiper.slideTo(22);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    swiper.allowSlideNext = false;
  };
  const goLast = () => {
    if (!swiper) return;

    // logger('goNext')
    swiper.allowSlideNext = true;
    swiper.slideTo(23);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    swiper.allowSlideNext = false;
  };

  const swiperConfig: SwiperProps = {
    direction: 'horizontal',
    allowSlideNext: false,
    allowSlidePrev: false,
    preventClicks: false,
    noSwiping: false,
    allowTouchMove: false,
    preventClicksPropagation: false,
    updateOnWindowResize: true,
  };

  // prevent user from going forward from pressing enter key
  // however it will allow enter key to go through if the event came from mui textarea
  // this is to allow the textarea to add new lines
  const handleEnterKey = async (event: any) => {
    if (
      event.key === 'Enter' &&
      !event.target.className.includes('MuiInputBase-inputMultiline')
    ) {
      event.preventDefault();
    }
  };

  return (
    <>
      {submissionFlag && <SubmitModal design={design} />}
      <div
        className={'swiper-container'}
        style={{
          background: design.colors.background,
          // background: rgb(0, 0, 0),
        }}
        onKeyDown={handleEnterKey}
      >
        {showProgress ? (
          <ProgressHeader
            progressValue={progress}
            steps={fields.length}
            design={design}
            reset={goBeginning}
          />
        ) : null}

        <Swiper
          autoHeight // for some reason causes speedup transition for start page?
          {...swiperConfig}
          onSwiper={(next) => {
            // logger('setSwiper')
            setSwiper(next);
          }}
        >
          {[
            startPage.show ? (
              <SwiperSlide key={'start'}>
                <Header design={design} />
                <FormPage
                  page={startPage}
                  className={'start-page'}
                  image={true}
                  design={design}
                  next={goNext}
                  prev={goPrev}
                  showProgress={setShowProgress}
                />
              </SwiperSlide>
            ) : undefined,
            ...fields
              .map((field, i) => {
                if (field.type === 'hidden') {
                  return null;
                }
                // branch - G7RnB7
                // path 1 start - rj2AP7
                // path 1 end - g1o98j
                // path 2 start - E1lv9j

                return (
                  <SwiperSlide key={field.id} className={'slide'}>
                    <Field
                      field={field}
                      focus={
                        swiper?.activeIndex === (startPage.show ? 1 : 0) + i
                      }
                      design={design}
                      save={async (values: { [key: string]: unknown }) => {
                        const fieldObj = {
                          field: field.id,
                          data: JSON.stringify(
                            values[field.id] !== undefined &&
                              values[field.id] !== null
                              ? values[field.id]
                              : '-'
                          ),
                        };

                        dispatch({
                          type: ACTIONS.ADD_SUBMISSION,
                          payload: fieldObj,
                        });

                        if (pathDone && isLive) {
                          setSubmissionFlag(true);
                        }
                      }}
                      next={(values?: any) => {
                        // 22 questions for path 1, 2 questions for path 2
                        if (!pathDone) {
                          if (field.id === 'G7RnB7') {
                            if (values.G7RnB7 === 'YES') {
                              setProgress(Math.floor((1 / 22) * 100));
                              goPath1();
                            } else {
                              setProgress(50);
                              goPath2();
                            }
                          } else if (field.id === 'g1o98j') {
                            setPathDone(true);
                            swiper!.allowSlidePrev = false;
                            setShowProgress(false);
                            setProgress(100);
                            goLast();
                          } else if (field.id === 'E1lv9j') {
                            setPathDone(true);
                            swiper!.allowSlidePrev = false;
                            setShowProgress(false);
                            setProgress(100);
                            goNext();
                          } else {
                            // prevent going back!
                            swiper!.allowSlidePrev = false;
                            setProgress(Math.floor(((i + 1) / 22) * 100));
                            goNext();
                          }
                        }
                      }}
                      prev={() => {
                        if (i !== 0) {
                          if (field.id === 'E1lv9j' || field.id === 'rj2AP7') {
                            goBeginning();
                          } else {
                            swiper!.allowSlideNext = false;
                            setProgress(Math.floor(((i - 1) / 22) * 100));
                            goPrev();
                          }
                        }
                      }}
                      slideIndex={i}
                      submissionCount={submissionCounter}
                      lastSlideIndex={lastSlideIndex}
                      totalQuestions={totalQuestions}
                    />
                  </SwiperSlide>
                );
              })
              .filter((e) => e !== null),
            endPage.show ? (
              <SwiperSlide key={'end'}>
                <Header design={design} />
                <FormPage
                  page={endPage}
                  className={'end-page'}
                  design={design}
                  next={goNext}
                  prev={goBeginning}
                  showProgress={setShowProgress}
                />
              </SwiperSlide>
            ) : undefined,
          ].filter((e) => !!e)}
        </Swiper>
      </div>
    </>
  );
};
